/**
 * <s-welcome-offer> — first-visit gift card pop-up.
 * Opens its inner <dialog> once per browser after a short delay, remembers it was seen
 * in localStorage, and copies the discount code on the `--copy-code` command.
 *
 * Attributes:
 *   data-code         — the discount code to copy
 *   data-copied-label — button text shown briefly after copying
 *   data-delay        — milliseconds of browsing before opening (default 10000)
 */
const STORAGE_KEY = 'yeezo-welcome-offer-seen';

class SWelcomeOffer extends HTMLElement {
  #ac;
  #timer;
  #resetTimer;

  connectedCallback() {
    this.#ac?.abort();
    this.#ac = new AbortController();
    const options = { signal: this.#ac.signal };
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.addEventListener('command', (event) => {
      if (event.command === '--copy-code') this.#copyCode(event.source);
    }, options);

    dialog.addEventListener('close', () => this.#markSeen(), options);

    if (this.#wasSeen()) return;
    // The delay counts browsing time across pages: the session start is kept in sessionStorage.
    const delay = Number(this.dataset.delay) || 10000;
    let remaining = delay;
    try {
      const startedAt = Number(window.sessionStorage.getItem(STORAGE_KEY + '-start')) || Date.now();
      window.sessionStorage.setItem(STORAGE_KEY + '-start', String(startedAt));
      remaining = Math.max(0, delay - (Date.now() - startedAt));
    } catch {
      // Without sessionStorage the delay simply starts on this page.
    }
    this.#timer = setTimeout(() => {
      if (!dialog.open && !document.querySelector('dialog[open]')) dialog.showModal();
      this.#markSeen();
    }, remaining);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
    clearTimeout(this.#resetTimer);
  }

  #wasSeen() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  #markSeen() {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // Storage can be unavailable in private modes; the pop-up then shows once per page view.
    }
  }

  async #copyCode(button) {
    const code = this.dataset.code;
    if (!code || !(button instanceof HTMLElement)) return;
    const label = button.querySelector('[data-copy-label]');
    const original = label?.textContent;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }

    if (label && this.dataset.copiedLabel) label.textContent = this.dataset.copiedLabel;
    clearTimeout(this.#resetTimer);
    this.#resetTimer = setTimeout(() => {
      if (label && original) label.textContent = original;
    }, 2000);
  }
}

if (!customElements.get('s-welcome-offer')) customElements.define('s-welcome-offer', SWelcomeOffer);
